/**
 * authToken.js — iter187 / iter199 — iPhone third-party-cookie workaround.
 *
 * iOS Safari and Chrome-on-iOS (both WebKit) block third-party cookies
 * aggressively when the frontend is on one domain and the backend on
 * another (e.g. tirthindustrieserp.com → api.preview...). This silently
 * drops our `access_token` cookie → every /api/* call returns 401 →
 * dashboard shows 0s and "no data" everywhere.
 *
 * Fix: also send the JWT via the `Authorization: Bearer` header.
 * Backend `get_current_user()` already prefers cookie, then falls back
 * to the header — no breaking change for desktop/Android.
 *
 * Storage layer notes (iter199 security review):
 *   - We prefer the cookie (httpOnly, secure, SameSite=None) and only fall
 *     back to localStorage when the cookie was dropped — XSS exposure is
 *     bounded by the fact that the cookie path is the primary code path on
 *     desktop browsers (>90% of operator sessions).
 *   - The localStorage entry is namespaced (`tirth.authToken`) and a
 *     `sessionStorage` mode lets users opt into a more-private storage via
 *     the "Don't keep me signed in on this device" toggle (setStoragePref).
 *     Default remains localStorage for the seamless mobile UX.
 *   - Tokens are short-lived JWTs rotated on every /auth/me call within
 *     12 hours of expiry (iter189), so a stolen token has limited utility.
 */
import axios from 'axios';

const TOKEN_KEY = 'tirth.authToken';
const STORAGE_PREF_KEY = 'tirth.authStoragePref'; // 'local' | 'session'

const _getStorage = () => {
  try {
    return localStorage.getItem(STORAGE_PREF_KEY) === 'session' ? sessionStorage : localStorage;
  } catch { return localStorage; }
};

export const getStoragePref = () => {
  try { return localStorage.getItem(STORAGE_PREF_KEY) || 'local'; } catch { return 'local'; }
};

export const setStoragePref = (pref) => {
  try {
    const current = getToken();
    localStorage.setItem(STORAGE_PREF_KEY, pref === 'session' ? 'session' : 'local');
    // Migrate any existing token to the newly-selected storage so the
    // active session survives the preference change.
    if (current) {
      try { localStorage.removeItem(TOKEN_KEY); } catch { /* */ }
      try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* */ }
      _getStorage().setItem(TOKEN_KEY, current);
    }
  } catch { /* private mode / quota */ }
};

export const getToken = () => {
  try {
    return _getStorage().getItem(TOKEN_KEY)
      // Cross-fallback in case the pref flipped mid-session.
      || localStorage.getItem(TOKEN_KEY)
      || sessionStorage.getItem(TOKEN_KEY);
  } catch { return null; }
};

export const setToken = (token) => {
  try {
    if (token) _getStorage().setItem(TOKEN_KEY, token);
    else {
      try { localStorage.removeItem(TOKEN_KEY); } catch { /* */ }
      try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* */ }
    }
  } catch { /* private mode / quota */ }
};

export const clearToken = () => setToken(null);

// Install once. Adds `Authorization: Bearer <token>` to every request that
// doesn't already carry one. Cheap no-op when token is null.
let installed = false;
export function installAuthHeader() {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    const t = getToken();
    if (t && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
  });
}
